export type TokenSet = {
  refreshToken: string | null;
  idToken: string;
  /** Epoch milliseconds. */
  expiresAt: number;
  /** The issuer/client that owns the refresh token. */
  source?: AuthSessionSource;
};

export type AuthSessionSource = "platform" | "passkey";

type Listener = (hasToken: boolean, identityChanged: boolean) => void;

type PersistedAuthSession = {
  source: AuthSessionSource;
  refreshToken: string;
  idToken: string;
  expiresAt: number;
};

const REFRESH_TOKEN_KEY = "tax_operations_refresh_token";
const SESSION_SOURCE_KEY = "tax_operations_auth_source";
const PLATFORM_SESSION_KEY = "tax_operations_platform_session";
const COGNITO_SESSION_KEY = "tax_operations_cognito_session";
const PASSKEY_SESSION_KEY = "tax_operations_passkey_session";

function browserStorage(kind: "local" | "session"): Storage | null {
  try {
    const root = typeof window === "undefined" ? globalThis : window;
    const storage = kind === "local" ? root.localStorage : root.sessionStorage;
    return storage ?? null;
  } catch {
    return null;
  }
}

/** UI cache boundary only; claims are never used to grant authorization. */
function sessionIdentity(token: string): string {
  try {
    const payload = token.split(".")[1];
    if (!payload) return token;
    const parsed = JSON.parse(atob(payload.replaceAll("-", "+").replaceAll("_", "/")));
    if (typeof parsed.iss === "string" && typeof parsed.sub === "string") {
      return JSON.stringify([parsed.iss, parsed.sub]);
    }
  } catch {
    /* An opaque token requires conservative cache isolation. */
  }
  return token;
}

function removeStored(storage: Storage | null, key: string): void {
  try {
    storage?.removeItem(key);
  } catch {
    /* Continue clearing other available stores. */
  }
}

/**
 * Platform password sessions live in origin-scoped localStorage so every tab
 * and a normal browser restart can send the same access token to Tax API.
 * Passkey sessions use the same durable origin storage. Legacy tab sessions
 * migrate on hydration. Refresh
 * tokens never attach to Tax API requests. The former Cognito session key is
 * only read so a leftover browser session is cleared, never reused.
 */
function createTokenStore() {
  let persistenceAvailable = true;
  let tokens: TokenSet | null = readHydratedTokens();
  let persistedRefreshToken: string | null = tokens?.refreshToken ?? readLegacyRefreshToken();
  let persistedSessionSource: AuthSessionSource | null =
    tokens?.source === "passkey" ? "passkey" : readPersistedSessionSource();
  const listeners = new Set<Listener>();

  function readLegacyRefreshToken(): string | null {
    try {
      return (
        browserStorage("local")?.getItem(REFRESH_TOKEN_KEY) ??
        browserStorage("session")?.getItem(REFRESH_TOKEN_KEY) ??
        null
      );
    } catch {
      persistenceAvailable = false;
      return null;
    }
  }

  function readPersistedSessionSource(): AuthSessionSource | null {
    try {
      const value =
        browserStorage("local")?.getItem(SESSION_SOURCE_KEY) ??
        browserStorage("session")?.getItem(SESSION_SOURCE_KEY);
      return value === "passkey" ? value : null;
    } catch {
      persistenceAvailable = false;
      return null;
    }
  }

  function parsePersistedSession(
    raw: string | null,
    expected?: AuthSessionSource,
  ): TokenSet | null {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as PersistedAuthSession;
      if (
        (expected !== undefined && parsed.source !== expected) ||
        !["platform", "passkey"].includes(parsed.source) ||
        typeof parsed.refreshToken !== "string" ||
        parsed.refreshToken.length === 0 ||
        typeof parsed.idToken !== "string" ||
        parsed.idToken.length === 0 ||
        typeof parsed.expiresAt !== "number" ||
        !Number.isFinite(parsed.expiresAt)
      ) {
        return null;
      }
      return {
        refreshToken: parsed.refreshToken,
        idToken: parsed.idToken,
        expiresAt: parsed.expiresAt,
        source: parsed.source,
      };
    } catch {
      return null;
    }
  }

  function readHydratedTokens(migrate = true): TokenSet | null {
    try {
      const fromLocal = parsePersistedSession(
        browserStorage("local")?.getItem(PLATFORM_SESSION_KEY) ?? null,
      );
      if (fromLocal) {
        if (migrate) {
          removeStored(browserStorage("session"), PASSKEY_SESSION_KEY);
          removeStored(browserStorage("session"), PLATFORM_SESSION_KEY);
          removeStored(browserStorage("session"), REFRESH_TOKEN_KEY);
          removeStored(browserStorage("session"), SESSION_SOURCE_KEY);
        }
        return fromLocal;
      }
      if (!migrate) return null;
      const fromPasskey = parsePersistedSession(
        browserStorage("session")?.getItem(PASSKEY_SESSION_KEY) ?? null,
        "passkey",
      );
      if (fromPasskey) {
        persistSession(fromPasskey);
        return fromPasskey;
      }
      const fromSession = parsePersistedSession(
        browserStorage("session")?.getItem(PLATFORM_SESSION_KEY) ?? null,
        "platform",
      );
      if (fromSession) {
        persistSession(fromSession);
        return fromSession;
      }
      removeStored(browserStorage("local"), COGNITO_SESSION_KEY);
      removeStored(browserStorage("session"), COGNITO_SESSION_KEY);
      return null;
    } catch {
      persistenceAvailable = false;
      return null;
    }
  }

  function persistSession(next: TokenSet | null): void {
    const local = browserStorage("local");
    const tab = browserStorage("session");
    try {
      if (next?.refreshToken && next.idToken) {
        const payload: PersistedAuthSession = {
          source: next.source ?? "platform",
          refreshToken: next.refreshToken,
          idToken: next.idToken,
          expiresAt: next.expiresAt,
        };
        if (!local) {
          persistenceAvailable = false;
          return;
        }
        local.setItem(PLATFORM_SESSION_KEY, JSON.stringify(payload));
        removeStored(tab, PASSKEY_SESSION_KEY);
        local?.setItem(REFRESH_TOKEN_KEY, next.refreshToken);
        removeStored(local, SESSION_SOURCE_KEY);
        removeStored(local, COGNITO_SESSION_KEY);
        removeStored(tab, PLATFORM_SESSION_KEY);
        removeStored(tab, COGNITO_SESSION_KEY);
        removeStored(tab, REFRESH_TOKEN_KEY);
        removeStored(tab, SESSION_SOURCE_KEY);
        return;
      }
      removeStored(tab, PASSKEY_SESSION_KEY);
      removeStored(local, PLATFORM_SESSION_KEY);
      removeStored(local, COGNITO_SESSION_KEY);
      removeStored(tab, PLATFORM_SESSION_KEY);
      removeStored(tab, COGNITO_SESSION_KEY);
      removeStored(local, REFRESH_TOKEN_KEY);
      removeStored(local, SESSION_SOURCE_KEY);
      removeStored(tab, REFRESH_TOKEN_KEY);
      removeStored(tab, SESSION_SOURCE_KEY);
    } catch {
      persistenceAvailable = false;
      // Private browsing/storage-disabled contexts still work for one session.
    }
  }

  function notify(identityChanged = false): void {
    for (const listener of listeners) listener(tokens !== null, identityChanged);
  }

  function sync(): void {
    if (!persistenceAvailable || !browserStorage("local")) return;
    const next = readHydratedTokens(false);
    if (!persistenceAvailable) return;
    if (JSON.stringify(next) === JSON.stringify(tokens)) return;
    const identityChanged =
      tokens !== null &&
      next !== null &&
      sessionIdentity(tokens.idToken) !== sessionIdentity(next.idToken);
    tokens = next;
    persistedRefreshToken = next?.refreshToken ?? null;
    persistedSessionSource = next?.source ?? null;
    notify(identityChanged);
  }

  function onStorage(event: StorageEvent): void {
    if (event.key === PLATFORM_SESSION_KEY || event.key === null) sync();
  }

  return {
    sync,
    snapshot(): TokenSet | null {
      return tokens;
    },
    set(next: TokenSet): void {
      tokens = next;
      persistedRefreshToken = next.refreshToken;
      persistedSessionSource = next.source === "passkey" ? "passkey" : null;
      persistSession(next);
      notify();
    },
    clear(): void {
      tokens = null;
      persistedRefreshToken = null;
      persistedSessionSource = null;
      persistSession(null);
      notify();
    },
    /** Returns null when absent or within the 30 s refresh skew. */
    getIdToken(now = Date.now()): string | null {
      if (!tokens) return null;
      if (tokens.expiresAt - now <= 30_000) return null;
      return tokens.idToken;
    },
    getRefreshToken(): string | null {
      return tokens?.refreshToken ?? persistedRefreshToken;
    },
    /** Returns the current ID token even when it is inside the refresh skew. */
    getStoredIdToken(): string | null {
      return tokens?.idToken ?? null;
    },
    /** Returns the owner of the current/persisted refresh token. */
    getSessionSource(): AuthSessionSource {
      return tokens?.source ?? persistedSessionSource ?? "platform";
    },
    expiresAt(): number | null {
      return tokens?.expiresAt ?? null;
    },
    hasToken(): boolean {
      return tokens !== null;
    },
    subscribe(listener: Listener): () => void {
      if (listeners.size === 0 && typeof window !== "undefined") {
        window.addEventListener("storage", onStorage);
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0 && typeof window !== "undefined") {
          window.removeEventListener("storage", onStorage);
        }
      };
    },
    /**
     * Diagnostics only. Never returns token material — this is what settings
     * and error reports are allowed to see.
     */
    describe(now = Date.now()): { present: boolean; expiresInSeconds: number | null } {
      return {
        present: tokens !== null,
        expiresInSeconds: tokens ? Math.max(0, Math.round((tokens.expiresAt - now) / 1000)) : null,
      };
    },
  };
}

export type TokenStore = ReturnType<typeof createTokenStore>;

export const tokenStore: TokenStore = createTokenStore();

export function createIsolatedTokenStore(): TokenStore {
  return createTokenStore();
}
